/* Asking for notifications, in words, before the browser asks in a dialog.
 *
 * A bare permission prompt with no explanation gets refused, and a refusal is
 * permanent — the browser will not ask twice, and there is no way for the app
 * to reopen that door. So the explanation comes first and the button is the
 * gesture that opens the real prompt.
 *
 * The other three states matter as much as the ask, and each says something
 * different:
 *
 *   · **denied** — tell them exactly where the switch is, because the app
 *     cannot reach it.
 *   · **broken** — permission is granted and subscribing still failed. This is
 *     the dangerous one: the person believes reminders are on and nothing will
 *     ever arrive. It says so, and offers to try again.
 *   · **unsupported / sw-failed** — a locked-down profile, a private window,
 *     an old iOS. Said once, quietly, rather than nagged about.
 *
 * Dismissal lives in sessionStorage: it should last the afternoon, not for
 * ever. Somebody who dismisses this and then wonders why nothing reminds them
 * should meet it again tomorrow.
 */

import { InfoToggle } from './Section';
import Icon from './Icon';
import { useEffect, useState } from 'react';
import { ask, ensure, startWorker } from './push';
import type { PushState } from './push';
import { readLocal, writeLocal } from '../api';

const DISMISSED = 'amon_push_dismissed';

export default function PushBanner({ signedIn }: { signedIn: boolean }) {
  const [state, setState] = useState<PushState | null>(null);
  const [reason, setReason] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem(DISMISSED) === '1'; } catch { return false; }
  });

  /* Registering the worker is what makes push possible at all, and it is worth
     doing whether or not permission has been granted: the worker also serves
     the offline shell. Doing it on sign-in rather than on mount means it is
     tied to having an account to register the device against. */
  useEffect(() => {
    if (!signedIn) { setState(null); return; }
    let alive = true;
    void startWorker().then(r => {
      if (!alive) return;
      setState(r.state);
      setReason(r.reason);
      /* Recorded so the day screen can say "reminders are off" where it
         matters, without every screen having to ask. */
      writeLocal('amon_push_state', r.state);
    });
    return () => { alive = false; };
  }, [signedIn]);

  if (!signedIn || !state) return null;
  if (state === 'granted') return null;
  /* Nothing can be done about these two from inside the page, and repeating it
     on every screen helps nobody. Said once per session. */
  if ((state === 'unsupported' || state === 'sw-failed') && dismissed) return null;
  if (state !== 'denied' && state !== 'broken' && dismissed) return null;

  const dismiss = () => {
    try { sessionStorage.setItem(DISMISSED, '1'); } catch { /* private mode */ }
    setDismissed(true);
  };

  const enable = async () => {
    setBusy(true);
    const r = await ask();
    setState(r.state);
    setReason(r.reason);
    writeLocal('amon_push_state', r.state);
    setBusy(false);
  };

  const retry = async () => {
    setBusy(true);
    const r = await ensure();
    setState(r.state);
    setReason(r.reason);
    setBusy(false);
  };

  const body = {
    default: {
      title: 'Turn on reminders',
      text: 'Amon plans your day and then tells you when each thing starts — ten minutes ' +
            'before, on the hour, and again if you have not begun. That only works if this ' +
            'device is allowed to notify you.',
      action: <button className="btn btn-primary btn-sm" onClick={enable} disabled={busy}>
        {busy ? 'Asking…' : 'Allow notifications'}
      </button>
    },
    denied: {
      title: 'Reminders are blocked for this site',
      text: 'Amon can plan your day but cannot tell you about it. The switch is in the ' +
            'browser, not in the app: click the icon at the left of the address bar → ' +
            'Notifications → Allow, then reload.',
      action: null
    },
    broken: {
      title: 'Reminders are allowed but not working',
      text: `Notifications are permitted for this site, and this device could not register ` +
            `for them${reason ? ` — ${reason}` : ''}. Nothing will arrive until this is fixed.`,
      action: <button className="btn btn-primary btn-sm" onClick={retry} disabled={busy}>
        {busy ? 'Trying…' : 'Try again'}
      </button>
    },
    unsupported: {
      title: 'This browser cannot show reminders',
      text: 'Amon will still plan your day, and the day screen still shows what is next — ' +
            'but nothing will reach you with the tab closed. On iPhone, add Amon to your ' +
            'home screen first and notifications become available.',
      action: null
    },
    'sw-failed': {
      title: 'Reminders cannot start on this device',
      text: `The background worker that delivers them would not start${reason ? ` — ${reason}` : ''}. ` +
            'A private window or a locked-down profile is the usual cause.',
      action: null
    }
  }[state];

  if (!body) return null;

  return (
    <div className={`notice push-banner${state === 'broken' ? ' notice-bad' : ''}`} role="status">
      <span className="notice-icon"><Icon name="bell" size={18} /></span>
      {/* The title on its own line; the why and the how behind the info
          button. The banner sits above every screen, so its resting height is
          paid on every visit — the explanation is needed once. */}
      <div>
        <p><strong>{body.title}</strong></p>
      </div>
      <div className="inline">
        {body.action}
        <InfoToggle about="reminders" text={body.text} />
        <button className="btn btn-ghost btn-icon" onClick={dismiss} aria-label="Not now" title="Not now"><Icon name="close" size={16} /></button>
      </div>
    </div>
  );
}

/** What the last check found, for screens that want to mention it. */
export const pushState = (): string | null => readLocal('amon_push_state');
